import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import "./CategoriesProducts.css";
import { BACKEND_URL } from "../../../config";

function CategoryProducts() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);

  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);

  const sortOptions = [
    { value: "", label: "Sort By" },
    { value: "low", label: "Price Low to High" },
    { value: "high", label: "Price High to Low" },
    { value: "name", label: "Name A-Z" },
  ];

  useEffect(() => {
    const handleOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);


  const [currentPage, setCurrentPage] = useState(1);

  const productsPerPage = 15;

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;

  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const totalPages = Math.ceil(
    filteredProducts.length / productsPerPage
  );

  useEffect(() => {
    setDataLoaded(false);
    setCurrentPage(1);

    const fetchData = async () => {
      try {
        const [pRes, cRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api.php?module=product`).then((r) => r.json()),
          fetch(`${BACKEND_URL}/api.php?module=category`).then((r) => r.json()),
        ]);

        if (pRes.success) {
          const categoryProducts = pRes.data.filter(
            (p) => p.category_slug === slug
          );
          setProducts(categoryProducts);
          setFilteredProducts(categoryProducts);
        }

        if (cRes.success) {
          const cat = cRes.data.find((c) => c.slug === slug);
          setCategory(cat);
        }
      } catch (err) {
        console.log(err);
      } finally {
        setDataLoaded(true);
      }
    };

    fetchData();
  }, [slug]);

  useEffect(() => {
    let updated = [...products];

    if (sortBy === "low") {
      updated.sort(
        (a, b) =>
          (a.packPricing?.[0]?.price || 0) -
          (b.packPricing?.[0]?.price || 0)
      );
    }

    if (sortBy === "high") {
      updated.sort(
        (a, b) =>
          (b.packPricing?.[0]?.price || 0) -
          (a.packPricing?.[0]?.price || 0)
      );
    }

    if (sortBy === "name") {
      updated.sort((a, b) =>
        a.productName.localeCompare(b.productName)
      );
    }

    setFilteredProducts(updated);
    setCurrentPage(1);

  }, [sortBy, products]);

  const safeJSON = (data) => {
    if (!data) return [];
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch {
        return [];
      }
    }
    return data;
  };

  const truncateAtSentence = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    const trimmed = text.substring(0, maxLength);
    const lastPeriod = trimmed.lastIndexOf(".");
    if (lastPeriod !== -1) {
      return trimmed.substring(0, lastPeriod + 1);
    }
    return trimmed.substring(0, trimmed.lastIndexOf(" "));
  };

  return (
    <div className="container">

      <Helmet>
        <title>
          {category?.metaTitle
            ? `${category.metaTitle} | RealGenericShop`
            : "Realgeneric shop"}
        </title>
        <meta name="description" content={category?.metaDescription} />
        <meta name="keywords" content={category?.keywords} />
         <link rel="canonical" href={`https://realgenericshop.com/categories/${slug}`} />
      </Helmet>

      <h1 className="title">{category?.categoryName}</h1>

      <div className="desc">
        <span>
          {(() => {
            const plainText = category?.description?.replace(/<[^>]+>/g, "") || "";
            const truncated = truncateAtSentence(plainText, 350);
            return plainText.length > 350 ? `${truncated} ` : plainText;
          })()}
        </span>

        {(category?.description?.replace(/<[^>]+>/g, "") || "").length > 350 && (
          <button
            className="read-more-btn"
            onClick={() => {
              document
                .getElementById("full-description")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Read More
          </button>
        )}
      </div>

      <div className="sort-filter">
        <div className="custom-select" ref={sortRef}>
          <button
            type="button"
            className={`custom-select-toggle ${sortOpen ? "open" : ""}`}
            onClick={() => setSortOpen(!sortOpen)}
          >
            <span>{sortOptions.find(o => o.value === sortBy)?.label || "Sort By"}</span>
            <svg className="custom-select-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {sortOpen && (
            <ul className="custom-select-menu">
              {sortOptions.map((opt) => (
                <li
                  key={opt.value}
                  className={`custom-select-option ${sortBy === opt.value ? "selected" : ""}`}
                  onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="category-product-grid">
        {!dataLoaded ? null : filteredProducts.length > 0 ? (
          currentProducts.map((p) => {
            const price = safeJSON(p.packPricing)?.[0]?.price || "N/A";

            return (
              <div className="product-card" key={p.id}>
                <img
                  src={
                    p.image?.startsWith("http")
                      ? p.image
                      : `${BACKEND_URL}/${p.image}`
                  }
                  alt={p.productName}
                  onClick={() => navigate(`/product/${p.slug}`)}
                />

                <h3 onClick={() => navigate(`/product/${p.slug}`)}>
                  {p.productName}
                </h3>

                <div className="product-rating">
                  <div className="product-stars">
                    <div className="product-star-bg">★★★★★</div>
                    <div
                      className="product-star-fill"
                      style={{
                        width: `${((Number(p.avg_rating) || 0) / 5) * 100}%`,
                      }}
                    >
                      ★★★★★
                    </div>
                  </div>
                  <span className="product-review-count">
                    ({p.review_count || 0})
                  </span>
                </div>

                <p className="product-price">
                  Starting at just <span>€{price}</span>
                </p>

                <button
                  className="btn"
                  onClick={() => navigate(`/product/${p.slug}`)}
                >
                  View Product
                </button>
              </div>
            );
          })
        ) : (
          <div className="no-products">
            <h2>No Products Found</h2>
          </div>
        )}
      </div>

        {totalPages > 1 && (
        <div className="pagination">
          
          {currentPage > 1 && (
            <button
              className="page-btn"
              onClick={() => {
                setCurrentPage(currentPage - 1);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              ← Prev
            </button>
          )}

          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              className={`page-btn ${
                currentPage === index + 1 ? "active" : ""
              }`}
              onClick={() => {
                setCurrentPage(index + 1);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              {index + 1}
            </button>
          ))}

          {currentPage < totalPages && (
            <button
              className="page-btn"
              onClick={() => {
                setCurrentPage(currentPage + 1);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Next →
            </button>
          )}

        </div>
      )}

      <div id="full-description" className="full-description">
        <div
          dangerouslySetInnerHTML={{
            __html: category?.description?.replace(/<p>.*?<\/p>/, ""),
          }}
        />
      </div>

    </div>
  );
}

export default CategoryProducts;